import env from "@/config/env";
import { ApiError } from "@/types/api";
import type {
  ClientDetails,
  ClientListItem,
  ClientMergeRecord,
  ClientMergeResult,
  ClientPerson,
  ClientSensitiveData,
  ClientsQueryParams,
  ConfirmPublicConsentPayload,
  ConsentChannel,
  ConsentRequest,
  CreateClientPayload,
  DuplicatePair,
  MergeClientsPayload,
  NameCasingFix,
  PaperConsentPayload,
  PublicConsent,
} from "@/types/clients";
import type { PaginatedResponse } from "@/types/rbac";
import { apiClient } from "./client";

function buildQueryString(params: ClientsQueryParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page));
  searchParams.set("pageSize", String(params.pageSize));
  if (params.search) {
    searchParams.set("search", params.search);
  }
  if (params.rodo && params.rodo !== "all") {
    searchParams.set("rodo", params.rodo);
  }
  if (params.agentId) {
    searchParams.set("agentId", params.agentId);
  }
  return searchParams.toString();
}

export const clientsApi = {
  getClients(params: ClientsQueryParams) {
    return apiClient.get<PaginatedResponse<ClientListItem>>(
      `/api/clients?${buildQueryString(params)}`,
    );
  },

  getClient(clientId: string) {
    return apiClient.get<ClientDetails>(`/api/clients/${clientId}`);
  },

  assignAgent(clientId: string, agentId: string) {
    return apiClient.post<ClientListItem>(`/api/clients/${clientId}/agents`, {
      agentId,
    });
  },

  uploadPaperConsent(clientId: string, p: PaperConsentPayload) {
    const form = new FormData();
    form.append("file", p.file);
    form.append("signedOn", p.signedOn);
    form.append("clientBase", String(p.clientBase));
    form.append("marketingPhone", String(p.marketingPhone));
    form.append("marketingSms", String(p.marketingSms));
    form.append("marketingEmail", String(p.marketingEmail));
    form.append("health", p.health === null ? "" : String(p.health));
    return apiClient.postFormData<ConsentRequest>(
      `/api/clients/${clientId}/paper-consents`,
      form,
    );
  },

  /** Opened in a new tab — the session cookie authorises the request. */
  consentDocumentUrl(requestId: string) {
    return `${env.apiBaseUrl}/api/consent-requests/${requestId}/document`;
  },

  getConsentChannels() {
    return apiClient.get<{ email: boolean; sms: boolean }>(
      "/api/clients/consent-channels",
    );
  },

  getClientPerson(clientId: string) {
    return apiClient.get<ClientPerson>(`/api/clients/${clientId}/person`);
  },

  revealSensitiveData(clientId: string) {
    return apiClient.post<ClientSensitiveData>(
      `/api/clients/${clientId}/sensitive-data`,
    );
  },

  createClient(payload: CreateClientPayload) {
    return apiClient.post<ClientListItem>("/api/clients", payload);
  },

  getDuplicates(agentId?: string) {
    const query = agentId ? `?agentId=${encodeURIComponent(agentId)}` : "";
    return apiClient.get<DuplicatePair[]>(`/api/clients/duplicates${query}`);
  },

  mergeClients(payload: MergeClientsPayload) {
    return apiClient.post<ClientMergeResult>("/api/clients/merge", payload);
  },

  getMerges() {
    return apiClient.get<ClientMergeRecord[]>("/api/clients/merges");
  },

  /** apply: false = preview only. */
  fixNameCasing(apply: boolean) {
    return apiClient.post<NameCasingFix>("/api/clients/name-casing", {
      apply,
    });
  },

  /** channel omitted = e-mail when the client has one, otherwise SMS. */
  requestConsent(
    clientId: string,
    includesHealth: boolean,
    channel?: ConsentChannel,
  ) {
    return apiClient.post<ConsentRequest>(
      `/api/clients/${clientId}/consent-requests`,
      { includesHealth, channel: channel ?? null },
    );
  },

  getConsentRequest(requestId: string) {
    return apiClient.get<ConsentRequest>(`/api/consent-requests/${requestId}`);
  },

  verifyCodes(requestId: string, codes: string[]) {
    return apiClient.post<ConsentRequest>(
      `/api/consent-requests/${requestId}/verify`,
      { codes },
    );
  },

  cancelRequest(requestId: string) {
    return apiClient.post<void>(`/api/consent-requests/${requestId}/cancel`);
  },
};

// The client's link has no session — plain fetch, no token refresh, no redirect to login.
async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${env.apiBaseUrl}/public/consents/${path}`, {
    ...init,
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    signal: AbortSignal.timeout(30_000),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      detail?: string;
    };
    throw new ApiError(body.detail ?? "Wystąpił błąd", response.status);
  }
  return (await response.json()) as T;
}

export const publicConsentApi = {
  get(token: string) {
    return publicRequest<PublicConsent>(encodeURIComponent(token));
  },

  confirm(token: string, payload: ConfirmPublicConsentPayload) {
    return publicRequest<PublicConsent>(
      `${encodeURIComponent(token)}/confirm`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  },
};
